'use client';

import React from 'react';
import styles from '@styles/components/error-boundary.module.scss';
import { useUser } from '../UserProvider';

interface ErrorDisplayProps {
  title: string;
  message: string;
  error: Error;
  onRetry?: () => void;
  retryLabel?: string;
}

const ErrorDisplay: React.FC<ErrorDisplayProps> = ({ title, message, error, onRetry, retryLabel = 'Try Again' }) => {
  const user = useUser();
  const [copied, setCopied] = React.useState(false);

  const getErrorContext = () => {
    const timestamp = new Date().toISOString();
    const url = typeof window !== 'undefined' ? window.location.href : 'N/A';
    const pathname = typeof window !== 'undefined' ? window.location.pathname : 'N/A';
    const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : 'N/A';
    const userInfo =
      user !== null
        ? `ID: ${user.id}\nEmail: ${user.email}\nCompany ID: ${user.owner_id?.owner_id}\nCompany Name: ${user.owner_id?.owner_name}`
        : 'Anonymous User';

    return `
=== ERROR CONTEXT ===
Date: ${timestamp}
Browser URL: ${url}
Internal Route: ${pathname}
Browser Info: ${userAgent}

=== USER INFO ===
${userInfo}

=== ERROR DETAILS ===
${error.name}: ${error.message}

${error.stack || 'No stack trace available'}
`;
  };

  const copyErrorDetails = () => {
    const context = getErrorContext();
    navigator.clipboard.writeText(context).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const emailSupport = () => {
    const prependedBody =
      'I have encountered an error while using evoIS. What I was trying to do when the error occurred:\n\n';

    const context = getErrorContext();
    const subject = encodeURIComponent(`evoIS Error Report`);
    const body = encodeURIComponent(prependedBody + '\n\n\n\nTechnical Details:\n```' + context + '```\n\n');
    const mailtoLink = `mailto:evo.CustomerSupport@pelibiothermal.com?cc=cloudservices@pelibiothermal.com&subject=${subject}&body=${body}`;
    window.location.href = mailtoLink;
  };

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        <div className={styles.iconWrapper}>
          <svg className={styles.icon} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
            <line x1="12" y1="8" x2="12" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            <circle cx="12" cy="16" r="1" fill="currentColor" />
          </svg>
        </div>

        <h1 className={styles.title}>{title}</h1>
        <p className={styles.message}>{message}</p>

        <details className={styles.details}>
          <summary className={styles.detailsSummary}>Error Details</summary>
          <div className={styles.detailsContent}>
            <p className={styles.errorName}>{error.name}</p>
            <p className={styles.errorMessage}>{error.message}</p>
            {error.stack && <pre className={styles.errorStack}>{error.stack}</pre>}
            <div className={styles.buttonGroup}>
              <button className={styles.copyButton} onClick={copyErrorDetails}>
                {copied ? 'Copied!' : 'Copy to Clipboard'}
              </button>
              <button className={styles.emailButton} onClick={emailSupport}>
                Email Support
              </button>
            </div>
          </div>
        </details>

        {onRetry && (
          <button className={styles.retryButton} onClick={onRetry}>
            {retryLabel}
          </button>
        )}
      </div>
    </div>
  );
};

export default ErrorDisplay;
